import React, { Component } from 'react';
import { Row, Col, Form, Icon, Input, Button, notification, Tooltip } from 'antd';
import FormControl from 'components/Forms/FormControl';
import { FormComponentProps } from 'antd/lib/form';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import ResetPassowrdFormHandler from '../ResetPasswordForm/ResetPasswordFormHandler';
import AuthContext from 'Context/AuthContext';
import { ChangePasswordBindingModel } from 'Core/Api/UserManagement/Api';
import ValidationUtility from 'Core/ValidationUtility';
import { InputColLayout, ButtonColLayout } from './ResetPasswordForm.Style';
import NotificationUtility from 'Core/NotificationUtility';
import { SubmitButton } from 'components/SubmitButton';

export interface Props extends FormComponentProps, RouteComponentProps {
  children?: React.ReactNode;
}

export interface State {
  password: string;
  newPassword: string;
  confirmPassword: string;
  confirmTrue: boolean;
  passChangeSuccess: false;
  errorMessage: string;
  passwordStrength: string;
  placeholder: string;
}

class BaseResetPasswordForm extends Component<Props, State> {
  static contextType = AuthContext;
  context!: React.ContextType<typeof AuthContext>;
  resetPassowrdFormHandler: ResetPassowrdFormHandler;

  constructor(props: Props) {
    super(props);
    this.resetPassowrdFormHandler = new ResetPassowrdFormHandler(props);
    this.state = {
      password: '',
      newPassword: '',
      confirmPassword: '',
      confirmTrue: false,
      passChangeSuccess: false,
      errorMessage: '',
      passwordStrength: '',
      placeholder: '',
    };
    this.validateToNextPassword = this.validateToNextPassword.bind(this);
    this.compareToFirstPassword = this.compareToFirstPassword.bind(this);
    this.validateNewPassword = this.validateNewPassword.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  validateNewPassword = (rule, value, callback) => {
    let password = value;
    let errorMessage;
    let capsCount, smallCount, numberCount, symbolCount;
    if (!password) {
      return null;
    }
    if (password.length < 8) {
      callback('password must be min 8 char');
    } else {
      capsCount = (password.match(/[A-Z]/g) || []).length;
      smallCount = (password.match(/[a-z]/g) || []).length;
      numberCount = (password.match(/[0-9]/g) || []).length;
      symbolCount = (password.match(/\W/g) || []).length;
      if (capsCount < 1) {
        errorMessage = 'must contain caps';
      } else if (smallCount < 1) {
        errorMessage = 'must contain small';
      } else if (numberCount < 1) {
        errorMessage = 'must contain number';
      } else if (symbolCount < 1) {
        errorMessage = 'must contain symbol';
      }
      callback(errorMessage);
    }
  };

  validateToNextPassword = (rule, value, callback) => {
    const { form } = this.props;
    if (value && this.state.confirmTrue) {
      form.validateFields(['confirmPassword'], { force: true });
    }
    callback();
  };

  compareToFirstPassword = (rule, value, callback) => {
    const { form } = this.props;
    if (value && value !== form.getFieldValue('newPassword')) {
      callback('Two passwords that you enter are inconsistent!');
    } else {
      callback();
    }
  };

  onFormSubmit(event: React.FormEvent) {
    event.preventDefault();
    this.props.form.validateFieldsAndScroll((err, values: ChangePasswordBindingModel) => {
      if (err) {
        return;
      }
      this.resetPassowrdFormHandler
        .ResetPassword(values)
        .then(() => {
          NotificationUtility.success(
            'Success',
            'Submission was successful, your password has been successfully changed',
            10,
            true
          );

          this.props.form.resetFields();
        })
        .catch(x => {
          NotificationUtility.error('Error', x.message ?? x, 30, true);
        });

    });
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const passwordPrompt = (
      <p style={{ textAlign: 'center', margin: 'auto' }}>
        {`A password must have at least 8 characters, with at least one number, one capital letter and one symbol`}
      </p>
    );

    return (
      <Row type="flex" justify="center">
        <Col span={20} offset={2}>
          <Form onSubmit={this.onFormSubmit} layout="vertical">
            {/* Reste Password*/}
            <Row gutter={10} type="flex" align="middle">
              <Col {...InputColLayout}>
                <FormControl
                  getFieldDecorator={getFieldDecorator}
                  rules={[{ required: true, message: 'Please enter Current Password' }]}
                  name="oldPassword"
                  label="Current Password">
                  <Input.Password
                    visibilityToggle={true}
                    prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Current Password"
                  />
                </FormControl>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col {...InputColLayout}>
                <FormControl
                  getFieldDecorator={getFieldDecorator}
                  rules={[
                    ...ValidationUtility.getStrongPassword(),
                    ...ValidationUtility.getRequired('Please enter your password'),
                    { validator: this.validateToNextPassword },
                  ]}
                  name="newPassword"
                  label={<><label>New Password</label><Tooltip placement="top" title={passwordPrompt}><span style={{ marginTop: "4px", marginLeft: "4px" }} ><Icon type='info-circle' /></span></Tooltip></>}
                >
                  <Input.Password
                    visibilityToggle={true}
                    prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="New Password"
                  />

                </FormControl>
              </Col>
            </Row>
            <Row gutter={10} type="flex" align="middle">
              <Col {...InputColLayout}>
                <FormControl
                  getFieldDecorator={getFieldDecorator}
                  rules={[
                    { required: true, message: 'Please confirm New Password' },
                    { validator: this.compareToFirstPassword },
                  ]}
                  name="confirmPassword"
                  label={<><label>Confirm New Password</label><Tooltip placement="top" title={passwordPrompt}><span style={{ marginTop: "4px", marginLeft: "4px" }} ><Icon type='info-circle' /></span></Tooltip></>}>
                  <Input.Password
                    visibilityToggle={true}
                    prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Confirm New Password"
                  />

                </FormControl>
              </Col>
            </Row>
            <Row>
              <Col {...ButtonColLayout}>
                <SubmitButton type="primary" htmlType="submit" className="reset-password-form-button" style={{ textAlign: "center" }}>Update Password</SubmitButton>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    );
  }
}

const ResetPasswordFormWithoutRouter = Form.create<Props>({
  name: 'ChangePasswordForm',
})(BaseResetPasswordForm);

const ResetPasswordForm = withRouter(ResetPasswordFormWithoutRouter);

export default ResetPasswordForm;
